import type { FocusAskCreatePayload } from '@atlassian/conversation-assistant-message-actions-api/focus-ask';

import { resourceIdFromIdentityAri } from './atlassian-refs';
import {
	emptyFocusAskTargetDateValue,
	parseRecordToFocusAskTargetDateValue,
} from './format-focus-ask-target-date';
import type {
	FocusAskLinkData,
	FocusAskTeamData,
	FocusAskUserData,
	FocusAskFormData,
} from './types';

const PRIORITIES: FocusAskCreatePayload['priority'][] = [
	'LOW',
	'MEDIUM',
	'HIGH',
	'HIGHEST',
	'CRITICAL',
];

/**
 * Checks if a value is a valid Focus Ask priority.
 */
function isFocusAskPriority(value: unknown): value is FocusAskCreatePayload['priority'] {
	return typeof value === 'string' && (PRIORITIES as string[]).includes(value);
}

/**
 * Picks a string field from an object by trying each key in order.
 */
function pickStringField(o: Record<string, unknown>, keys: string[]): string {
	for (const k of keys) {
		const v = o[k];
		if (typeof v === 'string' && v.trim()) {
			return v.trim();
		}
	}
	return '';
}

/**
 * Extracts the impacted work ARI from a bot action payload.
 *
 * The bot may send the ARI in two shapes:
 *   - Flat:   `{ impactedWorkId: 'ari:...' }`
 *   - Nested: `{ impactedWork: { id: 'ari:...' } }`
 *
 * This helper handles both without modifying the shared `pickStringField` utility.
 */
function normalizeImpactedWorkId(record: Record<string, unknown>): string {
	// Flat key — e.g. impactedWorkId: 'ari:...'
	if (typeof record.impactedWorkId === 'string' && record.impactedWorkId.trim()) {
		return record.impactedWorkId.trim();
	}
	// Nested object — e.g. impactedWork: { id: 'ari:...' }
	const nested = record.impactedWork;
	if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
		const id = (nested as Record<string, unknown>).id;
		if (typeof id === 'string' && id.trim()) {
			return id.trim();
		}
	}
	return '';
}

/**
 * Picks an optional URL field from an object.
 */
function pickOptionalUrl(o: Record<string, unknown>, keys: string[]): string | undefined {
	const s = pickStringField(o, keys);
	return s || undefined;
}

/**
 * Returns the first object element of an unknown array, or `null` if the input
 * is not a non-empty array of objects. Used to peel the first entry off the
 * `comments` / `links` wire arrays so the form (which only edits one of each
 * today) can hydrate from index 0.
 */
function firstObjectInArray(value: unknown): Record<string, unknown> | null {
	if (!Array.isArray(value) || value.length === 0) {
		return null;
	}
	const first = value[0];
	if (first && typeof first === 'object' && !Array.isArray(first)) {
		return first as Record<string, unknown>;
	}
	return null;
}

/**
 * Reads the user-editable comment string from the action payload. The
 * canonical shape is `comments: [{ data: string }]`. A legacy flat
 * `commentData: string` field is also accepted so older messages still render.
 */
function normalizeCommentData(data: unknown): string {
	if (typeof data === 'string') {
		return data.trim();
	}

	if (data && typeof data === 'object') {
		return JSON.stringify(data);
	}

	return '';
}

function normalizeComment(record: Record<string, unknown>): string {
	const firstComment = firstObjectInArray(record.comments);
	if (firstComment) {
		const normalized = normalizeCommentData(firstComment.data);
		if (normalized) {
			return normalized;
		}
	}

	return normalizeCommentData(record.commentData);
}

/**
 * Reads the related-link fields from the action payload. The canonical shape is
 * `links: [{ url: string, linkText?: string }]`. Legacy flat `linkUrl` /
 * `linkText` fields are also accepted so older messages still render.
 */
function normalizeLinks(record: Record<string, unknown>): FocusAskLinkData[] {
	if (Array.isArray(record.links)) {
		return record.links
			.map((link): FocusAskLinkData | null => {
				if (!link || typeof link !== 'object' || Array.isArray(link)) {
					return null;
				}
				const linkRecord = link as Record<string, unknown>;
				const url = typeof linkRecord.url === 'string' ? linkRecord.url.trim() : '';
				const linkText = typeof linkRecord.linkText === 'string' ? linkRecord.linkText.trim() : '';
				if (!url) {
					return null;
				}
				return linkText ? { url, linkText } : { url };
			})
			.filter((link): link is FocusAskLinkData => Boolean(link));
	}

	const url = typeof record.linkUrl === 'string' ? record.linkUrl.trim() : '';
	const linkText = typeof record.linkText === 'string' ? record.linkText.trim() : '';
	if (!url) {
		return [];
	}
	return linkText ? [{ url, linkText }] : [{ url }];
}

/**
 * Parses user / team ARI and returns ID.
 */
function parseParticipantId(
	nested: unknown,
	record: Record<string, unknown>,
	fallbackKeys: string[],
	resource: 'user' | 'team',
): string | null {
	if (typeof nested === 'string' && nested.trim()) {
		return resourceIdFromIdentityAri(nested.trim(), resource);
	}
	if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
		const o = nested as Record<string, unknown>;
		const raw =
			(typeof o.id === 'string' && o.id) ||
			(typeof o.accountId === 'string' && o.accountId) ||
			(typeof o.teamId === 'string' && o.teamId) ||
			(typeof o.ari === 'string' && o.ari);
		if (raw) {
			return resourceIdFromIdentityAri(raw, resource);
		}
	}
	for (const key of fallbackKeys) {
		const v = record[key];
		if (typeof v === 'string' && v.trim()) {
			return resourceIdFromIdentityAri(v.trim(), resource);
		}
	}
	return null;
}

/**
 * Parses the record and returns a FocusAskUserData.
 */
function parseFocusAskUser(
	nested: unknown,
	record: Record<string, unknown>,
	fallbackKeys: string[],
): FocusAskUserData | null {
	const id = parseParticipantId(nested, record, fallbackKeys, 'user');
	if (!id) {
		return null;
	}
	if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
		const o = nested as Record<string, unknown>;
		const name = pickStringField(o, ['name', 'displayName', 'publicName', 'nickname']) || id;
		const avatarUrl = pickOptionalUrl(o, ['avatarUrl', 'avatar', 'picture']);
		return avatarUrl ? { id, name, avatarUrl } : { id, name };
	}
	return { id, name: id };
}

/**
 * Parses the record and returns a FocusAskTeamData.
 */
function parseFocusAskTeam(
	nested: unknown,
	record: Record<string, unknown>,
	fallbackKeys: string[],
): FocusAskTeamData | null {
	const id = parseParticipantId(nested, record, fallbackKeys, 'team');
	if (!id) {
		return null;
	}
	if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
		const o = nested as Record<string, unknown>;
		const name = pickStringField(o, ['name', 'displayName', 'teamName', 'title']) || id;
		const avatarUrl = pickOptionalUrl(o, ['avatarUrl', 'avatar', 'picture']);
		return avatarUrl ? { id, name, avatarUrl } : { id, name };
	}
	return { id, name: id };
}

/**
 * Parses the data and returns a FocusAskFormData.
 */
export function normalizeFocusAskPayload(data: unknown): FocusAskFormData {
	if (!data || typeof data !== 'object') {
		return {
			name: '',
			description: '',
			justification: '',
			targetDate: emptyFocusAskTargetDateValue(),
			impactedWork: null,
			commentData: '',
			links: [],
			submitter: null,
			owner: null,
			submittingTeam: null,
			receivingTeam: null,
			priority: 'MEDIUM',
		};
	}
	const record = data as Record<string, unknown>;
	const impactedWorkId = normalizeImpactedWorkId(record);
	const links = normalizeLinks(record);

	return {
		name: typeof record.name === 'string' ? record.name : '',
		description: typeof record.description === 'string' ? record.description : '',
		justification: typeof record.justification === 'string' ? record.justification : '',
		targetDate: parseRecordToFocusAskTargetDateValue(record),
		impactedWork: impactedWorkId ? { id: impactedWorkId, link: '' } : null,
		commentData: normalizeComment(record),
		links,
		submitter: parseFocusAskUser(record.submitter, record, [
			'submitterId',
			'submitterAccountId',
			'submitterARI',
			'submitterAri',
		]),
		owner: parseFocusAskUser(record.owner, record, [
			'ownerId',
			'ownerAccountId',
			'ownerARI',
			'ownerAri',
		]),
		submittingTeam: parseFocusAskTeam(record.submittingTeam, record, [
			'submittingTeamId',
			'submittingTeamARI',
			'submittingTeamAri',
		]),
		receivingTeam: parseFocusAskTeam(record.receivingTeam, record, [
			'receivingTeamId',
			'receivingTeamARI',
			'receivingTeamAri',
		]),
		priority: isFocusAskPriority(record.priority) ? record.priority : 'MEDIUM',
	};
}
